/**
 * 공급사 입금 계좌와 증빙 서류
 * ---------------------------------------------------------------------------
 * ## 왜 필요한가
 * 결제 수단이 **계좌이체**인데, 정작 어느 계좌로 넣는지가 고객 화면 어디에도
 * 없었다. 고객은 청구서를 받고 전화를 걸어 물어야 했고, 받아 적는 동안
 * 한 자리가 틀리면 **돈이 엉뚱한 곳으로 간다.** 세금계산서를 끊으려면
 * 공급자 사업자등록증도 필요한데 그것도 메일로 주고받고 있었다.
 *
 * 여기서 한 번 적어 두면 고객의 <b>구독 → 사용량·청구 → 결제 수단</b> 에
 * 그대로 뜨고, 사업자등록증·통장사본은 고객이 직접 내려받는다.
 *
 * ## 서류는 기업 서류 장치를 그대로 쓴다
 * 공급사도 테넌트다(slug=system). 올리는 순서도 기업 서류와 같다 —
 * 자리 잡기 → 전송 → 확정. 용량·확장자 정책과 바이러스 검사가 그대로 걸린다.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Text, Paragraph } = Typography;

interface Bank {
  bankName: string;
  accountNo: string;
  accountHolder: string;
  memo: string;
  missing: string[];
}

interface SupplierDoc {
  documentId: string;
  docType: string;
  docTypeName: string | null;
  memo: string | null;
  fileName: string;
  byteSize: number;
  status: string;
  createdAt: string;
}

interface UploadTicket {
  fileId: string;
  uploadUrl: string;
}

/** 첨부 결과. 한 장만 두는 종류를 바꿔치기했으면 무엇을 치웠는지 들어 있다. */
interface AttachResult {
  replaced: Array<{ documentId: string; fileName: string }>;
}

/** 고객에게 보이는 두 종류. 나머지는 올려도 고객 화면에 나가지 않는다. */
const PUBLIC_TYPES: Array<{ value: string; label: string; hint: string }> = [
  {
    value: 'BIZ_LICENSE',
    label: '사업자등록증',
    hint: '고객이 계산서를 받을 때 공급자 확인용으로 요청합니다.',
  },
  {
    value: 'BANKBOOK',
    label: '통장 사본',
    hint: '고객이 이체 전에 예금주를 확인합니다.',
  },
];

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)}${units[i]}`;
}

export function SupplierInfoPanel() {
  const { message } = App.useApp();
  const client = useQueryClient();
  const [bankOpen, setBankOpen] = useState(false);
  const [uploadType, setUploadType] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [bankForm] = Form.useForm<Bank>();

  const bank = useQuery<Bank, NormalizedError>({
    queryKey: ['admin', 'billing', 'supplier-bank'],
    queryFn: async () => (await apiClient.get('/admin/billing/supplier/bank')).data,
  });

  const docs = useQuery<SupplierDoc[], NormalizedError>({
    queryKey: ['admin', 'billing', 'supplier-docs'],
    queryFn: async () => (await apiClient.get('/admin/billing/supplier/documents')).data,
  });

  // 조회값을 그대로 폼에 붓고 저장할 때 통째로 되보낸다. 비어 있는 칸이
  // null 로 와도 서버가 「지우라」로 읽으므로 저장이 막히지 않는다.
  useEffect(() => {
    if (bank.data) bankForm.setFieldsValue(bank.data);
  }, [bank.data, bankForm]);

  const saveBank = useMutation<Bank, NormalizedError, Partial<Bank>>({
    mutationFn: async (v) => (await apiClient.patch('/admin/billing/supplier/bank', v)).data,
    onSuccess: () => {
      message.success('입금 계좌를 저장했습니다. 고객 화면에 바로 반영됩니다.');
      setBankOpen(false);
      void client.invalidateQueries({ queryKey: ['admin', 'billing', 'supplier-bank'] });
    },
    onError: (e) => message.error(errorText(e)),
  });

  const removeDoc = useMutation<void, NormalizedError, string>({
    mutationFn: async (documentId) => {
      await apiClient.delete(`/admin/billing/supplier/documents/${documentId}`);
    },
    onSuccess: () => {
      message.success('서류를 뗐습니다.');
      void client.invalidateQueries({ queryKey: ['admin', 'billing', 'supplier-docs'] });
    },
    onError: (e) => message.error(errorText(e)),
  });

  /**
   * 올리기 — 자리 잡기 → 전송 → 확정.
   *
   * 확정까지 끝나야 목록에 나온다. 중간에 끊기면 저장소에 미확정 파일만
   * 남고 서류로는 등록되지 않는다 — 올라가지도 않은 서류가 「첨부됨」으로
   * 보이는 것보다 낫다.
   */
  const upload = async (file: File, docType: string): Promise<void> => {
    /*
     * 이미 있으면 **올리기 전에** 묻는다. 서버도 묻지 않은 바꿔치기를
     * 거절하지만(409), 거절을 보고 묻는 순서로 만들면 그때는 이미 파일이
     * 저장소에 올라가 있다.
     */
    const existing = rows.filter((r) => r.docType === docType);
    if (existing.length > 0) {
      const label = PUBLIC_TYPES.find((t) => t.value === docType)?.label ?? '서류';
      const ok = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: `이미 ${label}이(가) 첨부되어 있습니다`,
          content: (
            <Space direction="vertical" size={4}>
              <Text>
                이 종류는 <b>한 장만 보관</b>합니다. 새로 올리시면{' '}
                <b>기존 파일을 삭제하고 이 파일로 대체</b>합니다.
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                지워질 파일: {existing.map((r) => r.fileName).join(' · ')}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                고객 화면에도 바로 새 파일이 나갑니다.
              </Text>
            </Space>
          ),
          okText: '대체합니다',
          okButtonProps: { danger: true },
          cancelText: '그만두기',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!ok) return;
    }

    setUploading(true);
    try {
      const { data: ticket } = await apiClient.post<UploadTicket>(
        '/admin/billing/supplier/documents/upload-url',
        { fileName: file.name, contentType: file.type || 'application/octet-stream', byteSize: file.size },
      );

      // 저장소가 주는 전송 주소로 브라우저가 직접 보낸다. API 서버를 거치지
      // 않으므로 큰 파일에도 API 가 막히지 않는다.
      const res = await fetch(ticket.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!res.ok) throw new Error(`파일 전송에 실패했습니다 (HTTP ${res.status})`);

      const { data: attached } = await apiClient.post<AttachResult>(
        '/admin/billing/supplier/documents',
        { fileId: ticket.fileId, docType, replaceExisting: existing.length > 0 },
      );
      const replaced = attached?.replaced ?? [];
      message.success(
        replaced.length > 0
          ? `${replaced.map((r) => r.fileName).join(' · ')} 을(를) 삭제하고 ${file.name} 으로 대체했습니다.`
          : '서류를 첨부했습니다.',
      );
      setUploadType(null);
      await client.invalidateQueries({ queryKey: ['admin', 'billing', 'supplier-docs'] });
    } catch (err) {
      message.error(err instanceof Error ? err.message : errorText(err as NormalizedError));
    } finally {
      setUploading(false);
    }
  };

  const download = async (documentId: string): Promise<void> => {
    try {
      const { data } = await apiClient.post<{ url: string }>(
        `/admin/billing/supplier/documents/${documentId}/download-url`,
      );
      window.open(data.url, '_blank', 'noopener');
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  const b = bank.data;
  const rows = docs.data ?? [];
  const latestOf = (docType: string) => rows.find((r) => r.docType === docType) ?? null;

  const columns: ColumnsType<SupplierDoc> = [
    {
      title: '종류',
      key: 'type',
      width: 150,
      render: (_, r) => (
        <Space size={6}>
          <span>{r.docTypeName ?? r.docType}</span>
          {PUBLIC_TYPES.some((t) => t.value === r.docType) && latestOf(r.docType) === r && (
            <Tag color="blue">고객 공개</Tag>
          )}
        </Space>
      ),
    },
    { title: '파일', dataIndex: 'fileName', ellipsis: true },
    {
      title: '크기',
      dataIndex: 'byteSize',
      width: 90,
      align: 'right',
      render: (v: number) => formatBytes(v),
    },
    {
      title: '상태',
      dataIndex: 'status',
      width: 90,
      render: (v: string) =>
        v === 'READY' ? <Tag color="green">정상</Tag> : <Tag color="orange">{v}</Tag>,
    },
    {
      title: '올린 날',
      dataIndex: 'createdAt',
      width: 160,
      render: (v: string) => new Date(v).toLocaleString('ko-KR'),
    },
    {
      title: '관리',
      key: 'act',
      width: 150,
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" type="link" icon={<DownloadOutlined />}
            onClick={() => void download(r.documentId)}>
            받기
          </Button>
          <Popconfirm
            title="이 서류를 뗍니까?"
            description="고객 화면에서도 사라집니다."
            okText="뗍니다"
            cancelText="취소"
            onConfirm={() => removeDoc.mutate(r.documentId)}
          >
            <Button size="small" type="link" danger>
              떼기
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* ---- 입금 계좌 ---- */}
      <Card
        size="small"
        title="입금 계좌 (고객사가 이용료를 넣는 곳)"
        loading={bank.isLoading}
        extra={
          <Button size="small" onClick={() => setBankOpen(true)}>
            고치기
          </Button>
        }
      >
        {b && b.missing.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 10 }}
            message={`${b.missing.join(' · ')} 이(가) 비어 있어 고객 화면에 계좌가 뜨지 않습니다`}
            description="계좌이체로 받는 동안에는 이 값이 없으면 고객이 어디로 넣어야 할지 알 수 없습니다."
          />
        )}
        <Descriptions size="small" column={3}>
          <Descriptions.Item label="은행">
            {b?.bankName || <Text type="danger">입력 필요</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="계좌번호">
            {b?.accountNo || <Text type="danger">입력 필요</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="예금주">
            {b?.accountHolder || <Text type="danger">입력 필요</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="입금 안내" span={3}>
            {b?.memo || <Text type="secondary">없음</Text>}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* ---- 증빙 서류 ---- */}
      <Card
        size="small"
        title="증빙 서류"
        loading={docs.isLoading}
        extra={
          <Space size={6}>
            {PUBLIC_TYPES.map((t) => (
              <Button key={t.value} size="small" icon={<UploadOutlined />}
                onClick={() => setUploadType(t.value)}>
                {t.label}
              </Button>
            ))}
          </Space>
        }
      >
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
          <b>사업자등록증</b>과 <b>통장 사본</b>은 고객사의{' '}
          <b>구독 → 사용량·청구 → 결제 수단</b> 화면에서 직접 내려받습니다. 두 종류는{' '}
          <b>한 장만 보관</b>합니다 — 새로 올리시면 기존 파일을 지우고 대체하며, 지우기 전에
          한 번 여쭤 봅니다. 옛 등록증까지 늘어놓으면 어느 것이 유효한지 고객이 고르게
          만드는 셈입니다.
        </Paragraph>
        {PUBLIC_TYPES.filter((t) => !latestOf(t.value)).map((t) => (
          <Alert
            key={t.value}
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message={`${t.label}이(가) 첨부되지 않았습니다`}
            description={t.hint}
          />
        ))}
        <Table<SupplierDoc>
          rowKey="documentId"
          size="small"
          columns={columns}
          dataSource={rows}
          pagination={false}
          locale={{ emptyText: '첨부된 서류가 없습니다.' }}
        />
      </Card>

      {/* ---- 계좌 고치기 ---- */}
      <Modal
        open={bankOpen}
        title="입금 계좌"
        okText="저장"
        cancelText="취소"
        confirmLoading={saveBank.isPending}
        onCancel={() => setBankOpen(false)}
        onOk={() => void bankForm.validateFields().then((v) => saveBank.mutate(v))}
        width={520}
      >
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          여기 적는 그대로 <b>고객 화면과 청구 안내에 찍힙니다.</b> 통장에 적힌 대로
          넣어 주세요.
        </Paragraph>
        <Form form={bankForm} layout="vertical" size="small">
          <Form.Item name="bankName" label="은행명" rules={[{ required: true, message: '은행명을 입력하세요' }]}>
            <Input placeholder="국민은행" maxLength={40} />
          </Form.Item>
          <Form.Item
            name="accountNo"
            label="계좌번호"
            rules={[{ required: true, message: '계좌번호를 입력하세요' }]}
            extra="하이픈을 넣어 적어도 됩니다 — 적은 그대로 고객에게 보입니다."
          >
            <Input placeholder="123456-01-123456" maxLength={60} />
          </Form.Item>
          <Form.Item name="accountHolder" label="예금주" rules={[{ required: true, message: '예금주를 입력하세요' }]}>
            <Input placeholder="(주)워크앤아이티" maxLength={60} />
          </Form.Item>
          <Form.Item
            name="memo"
            label="입금 안내 문구"
            extra="고객 화면에 함께 뜹니다. 예: 입금자명은 사업자등록번호로 적어 주세요."
          >
            <Input.TextArea rows={2} maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>

      {/* ---- 서류 올리기 ---- */}
      <Modal
        open={uploadType !== null}
        title={PUBLIC_TYPES.find((t) => t.value === uploadType)?.label ?? '서류'}
        footer={null}
        onCancel={() => setUploadType(null)}
        width={460}
      >
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          {PUBLIC_TYPES.find((t) => t.value === uploadType)?.hint}
          <br />
          올리면 <b>고객사 모두가 내려받을 수 있습니다.</b> 개인 정보가 함께 찍힌 서류가
          아닌지 확인해 주세요.
        </Paragraph>
        <Upload.Dragger
          accept=".pdf,.png,.jpg,.jpeg"
          showUploadList={false}
          disabled={uploading}
          beforeUpload={(file) => {
            if (uploadType) void upload(file, uploadType);
            // antd 의 자체 업로드를 막는다 — 전송은 위에서 직접 한다.
            return false;
          }}
        >
          <p style={{ margin: '12px 0' }}>
            {uploading ? '올리는 중…' : '여기로 끌어다 놓거나 눌러서 고르세요'}
          </p>
          <Text type="secondary" style={{ fontSize: 12 }}>
            PDF · PNG · JPG
          </Text>
        </Upload.Dragger>
      </Modal>
    </Space>
  );
}
